import type { Task } from 'ember-concurrency'
import type { TOC } from '@ember/component/template-only'
import { ErrorsUnexpected } from 'teacher-ui-client/components/errors/unexpected'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'

interface Args {
  /**
   * The ember-concurrency task that the template reacts to
   */
  task: Task<unknown, unknown[]>
  /**
   * A class string applied to the initial and error blocks. Useful if you want to apply spacing to them.
   */
  initialLoadingClass?: string
}

interface Signature {
  Args: Args
  Blocks: {
    error: [unknown, boolean]
    result: [unknown, boolean]
    initial: []
    default: [any, boolean]
  }
}

/**
 * This component helps to manage the common UI pattern around tasks, where the UI we show depends on the state of the
 * last completed task. This way, we can show the last result (errored, successful or empty) while a new result is loading.
 * Each state presents the loading spinner differently.
 *
 * The benefits of this is that the page does not layout shift while new data is loading.
 *
 * Note that you need to perform the task elsewhere. This only handles how to present a task based on a tasks' state.
 *
 * Usage:
 *
 * SIMPLE - single block for how you want to handle the happy path (value available). initial and error states taken care of
 * <TaskState @task={{@fetchDataTask}} as |lastValue isRunning|>
 *   <TableComponent
 *     @data={{lastValue}}
 *     @showSpinnerOverlay={{isRunning}}
 *   />
 * </TaskState>
 *
 * ADVANCED - do whatever you want.
 * <TaskState @task={{@fetchDataTask}}>
 *   <:initial>
 *     loading! I have no data to display yet
 *   </:initial>
 *   <:result as |lastValue isRunning|>
 *     My last task was successful, its data was {{lastValue}} {{if isRunning "and I am loading more data"}}
 *   </:result>
 *   <:error as |lastError isRunning|>
 *     My last task errored, its error was {{lastError}} {{if isRunning "and I am loading more data"}}
 *   </:error>
 * </TaskState>
 */

export const TaskState: TOC<Signature> = <template>
  {{#if @task.lastComplete.isError}}
    {{! Last task errored, no value }}
    {{#if (has-block "error")}}
      {{yield @task.lastComplete.error @task.isRunning to="error"}}
    {{else}}
      <div data-test-task-state-error class={{@initialLoadingClass}}>
        {{#if @task.isRunning}}
          <FidgetSpinnerWaveComponent @centered={{true}} />
        {{else}}
          <ErrorsUnexpected @compact={{true}} />
        {{/if}}
      </div>
    {{/if}}
  {{else if @task.lastComplete.isSuccessful}}
    {{! Last task was successful. Up to the implementation to handle how to display data and loading & empty states }}
    {{#if (has-block "result")}}
      {{yield @task.lastComplete.value @task.isRunning to="result"}}
    {{else if (has-block)}}
      {{yield @task.lastComplete.value @task.isRunning}}
    {{/if}}
  {{else}}
    {{! First run, no value yet, just show a spinner }}
    {{#if (has-block "initial")}}
      {{yield to="initial"}}
    {{else}}
      <div data-test-task-state-initial class={{@initialLoadingClass}}>
        <FidgetSpinnerWaveComponent @centered={{true}} />
      </div>
    {{/if}}
  {{/if}}
</template>

export default TaskState
